/* eslint-disable */
import React, { useEffect, useState } from 'react';
import {
  MessageSquare,
  Download,
  Search,
  Trash2,
  Mail,
  CheckCircle,
  Archive,
  Reply,
  X,
  Sparkles,
  Clock,
} from 'lucide-react';
import axiosClient from '../api/axiosClient';
import toast from 'react-hot-toast';

export default function MessagesManager() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [replyingMessage, setReplyingMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [generatingAi, setGeneratingAi] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, [statusFilter, search]);

  const fetchMessages = async () => {
    try {
      const res = await axiosClient.get(`/messages?status=${statusFilter}&search=${search}`);
      setMessages(res.data);
    } catch (err) {
      toast.error('Failed to load contact messages.');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status, replyNote = null) => {
    try {
      await axiosClient.put(`/messages/${id}`, { status, replyNote });
      toast.success(`Message marked as ${status}`);
      fetchMessages();
      if (replyingMessage) setReplyingMessage(null);
    } catch (err) {
      toast.error('Failed to update message status.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to permanently delete this message?')) {
      try {
        await axiosClient.delete(`/messages/${id}`);
        toast.success('Message deleted.');
        fetchMessages();
      } catch (err) {
        toast.error('Failed to delete message.');
      }
    }
  };

  const handleAiDraftReply = async (message) => {
    setGeneratingAi(true);
    try {
      const res = await axiosClient.post('/ai/generate', {
        action: 'rewrite',
        prompt: `Write a courteous, professional IT service response to customer inquiry from ${message.name}: "${message.message}"`,
      });
      setReplyText(res.data.output);
      toast.success('AI reply drafted!');
    } catch (err) {
      toast.error('Failed to generate AI response.');
    } finally {
      setGeneratingAi(false);
    }
  };

  const handleExportCsv = () => {
    const csvContent = 'data:text/csv;charset=utf-8,' +
      ['Name,Email,Phone,Subject,Message,Status,Date']
        .concat(messages.map(m => `"${m.name}","${m.email}","${m.phone || ''}","${m.subject || ''}","${m.message.replace(/"/g, '""')}","${m.status}","${m.createdAt}"`))
        .join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `fajtech_messages_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Messages exported to CSV.');
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-white tracking-tight flex items-center space-x-2">
            <MessageSquare className="text-blue-500" />
            <span>Contact Messages & Inquiries</span>
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Review incoming leads, customer support requests, and reply with AI assistance.
          </p>
        </div>

        <button
          onClick={handleExportCsv}
          className="px-4 py-2.5 rounded-xl font-bold text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 flex items-center space-x-2 transition-colors self-start sm:self-auto shadow-md"
        >
          <Download size={16} />
          <span>Export CSV</span>
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-900/60 p-4 rounded-2xl border border-slate-800">
        <div className="flex items-center space-x-1.5 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
          {['', 'UNREAD', 'READ', 'REPLIED', 'ARCHIVED'].map((st) => (
            <button
              key={st}
              onClick={() => setStatusFilter(st)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                statusFilter === st ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:bg-slate-800'
              }`}
            >
              {st || 'All Messages'}
            </button>
          ))}
        </div>

        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
          <input
            type="text"
            placeholder="Search by name, email, topic..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white text-xs placeholder-slate-500 focus:outline-none focus:border-blue-500"
          />
        </div>
      </div>

      {/* Messages Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center py-16 bg-slate-900/40 rounded-2xl border border-slate-800/60">
          <MessageSquare className="mx-auto text-slate-600 mb-3" size={40} />
          <h3 className="text-sm font-bold text-slate-300">No messages found</h3>
          <p className="text-xs text-slate-500 mt-1">Inquiries submitted through the website will appear here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {messages.map((m) => (
            <div
              key={m.id}
              className={`bg-slate-900 border rounded-2xl p-5 shadow-xl flex flex-col justify-between transition-all duration-200 ${
                m.status === 'UNREAD'
                  ? 'border-blue-500/60 ring-1 ring-blue-500/20'
                  : 'border-slate-800 hover:border-slate-700'
              }`}
            >
              <div className="space-y-3">
                <div className="flex items-start justify-between border-b border-slate-800/80 pb-3">
                  <div className="space-y-0.5">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-bold text-sm text-white">{m.name}</h3>
                      <span
                        className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                          m.status === 'UNREAD'
                            ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                            : m.status === 'REPLIED'
                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                            : 'bg-slate-800 text-slate-400'
                        }`}
                      >
                        {m.status}
                      </span>
                    </div>
                    <p className="text-xs text-blue-400">{m.email}</p>
                    {m.phone && <p className="text-[11px] text-slate-400 font-mono">{m.phone}</p>}
                  </div>

                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => {
                        setReplyingMessage(m);
                        setReplyText('');
                      }}
                      className="p-1.5 rounded-lg bg-slate-800 hover:bg-blue-600/20 text-slate-300 hover:text-blue-400 transition-colors"
                      title="Reply"
                    >
                      <Reply size={14} />
                    </button>
                    <button
                      onClick={() => updateStatus(m.id, m.status === 'ARCHIVED' ? 'READ' : 'ARCHIVED')}
                      className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
                      title={m.status === 'ARCHIVED' ? 'Unarchive' : 'Archive'}
                    >
                      <Archive size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(m.id)}
                      className="p-1.5 rounded-lg bg-slate-800 hover:bg-rose-600/20 text-slate-300 hover:text-rose-400 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-xs">
                  <div className="font-semibold text-slate-300">
                    Subject: <span className="text-white font-normal">{m.subject || 'General Inquiry'}</span>
                  </div>

                  <div className="p-3.5 rounded-xl bg-slate-950/80 text-slate-300 border border-slate-800/60 leading-relaxed whitespace-pre-line text-xs">
                    "{m.message}"
                  </div>

                  {m.replyNote && (
                    <div className="p-3 rounded-xl bg-emerald-950/20 border border-emerald-800/30 text-emerald-300 text-xs">
                      <span className="font-bold block mb-0.5">Staff Reply Note:</span>
                      {m.replyNote}
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-3 mt-3 border-t border-slate-800/80 flex items-center justify-between text-[11px] text-slate-400">
                <span className="flex items-center space-x-1 font-mono">
                  <Clock size={12} />
                  <span>{new Date(m.createdAt).toLocaleString()}</span>
                </span>

                {m.status === 'UNREAD' && (
                  <button
                    onClick={() => updateStatus(m.id, 'READ')}
                    className="text-blue-400 hover:text-blue-300 font-semibold"
                  >
                    Mark as Read
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reply Modal */}
      {replyingMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-xl shadow-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-white">Reply to {replyingMessage.name}</h2>
                <p className="text-xs text-blue-400 font-mono">{replyingMessage.email}</p>
              </div>
              <button
                onClick={() => setReplyingMessage(null)}
                className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs">
              <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 text-slate-400 italic">
                "{replyingMessage.message}"
              </div>

              <div className="flex items-center justify-between bg-blue-950/30 border border-blue-800/40 p-3 rounded-xl">
                <div>
                  <span className="text-xs font-bold text-blue-300">AI Response Assistant</span>
                  <p className="text-[11px] text-slate-400">Draft a tailored, courteous reply instantly.</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleAiDraftReply(replyingMessage)}
                  disabled={generatingAi}
                  className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold flex items-center space-x-1.5 shadow-md"
                >
                  <Sparkles size={13} />
                  <span>{generatingAi ? 'Drafting...' : 'Auto-Draft'}</span>
                </button>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">Reply Content / Note</label>
                <textarea
                  rows={6}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type your response or log notes here..."
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-800 bg-slate-950/40 flex items-center justify-between">
              <a
                href={`mailto:${replyingMessage.email}?subject=${encodeURIComponent('Re: ' + (replyingMessage.subject || 'Inquiry'))}&body=${encodeURIComponent(replyText)}`}
                className="text-xs text-blue-400 hover:underline font-semibold flex items-center space-x-1"
              >
                <Mail size={14} />
                <span>Open in Email Client</span>
              </a>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setReplyingMessage(null)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={() => updateStatus(replyingMessage.id, 'REPLIED', replyText)}
                  className="px-4 py-2 rounded-xl text-xs font-bold bg-blue-600 hover:bg-blue-500 text-white shadow-md flex items-center space-x-1.5"
                >
                  <CheckCircle size={15} />
                  <span>Mark as Replied</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
